/* global globalThis */
import { useState, useEffect } from "react";
import {
  Routes,
  Route,
  Navigate,
  useNavigate,
  useLocation,
} from "react-router-dom";
import { useDispatch, connect, useSelector } from "react-redux";
import PropTypes from "prop-types";
import {
  ThemeProvider,
  CssBaseline,
  Snackbar,
  Alert,
  useMediaQuery,
} from "@mui/material";
import theme from "./theme";
import "react-toastify/dist/ReactToastify.css";
import "./styles/overrideReactToast.css";
import SessionExpired from "./components/SessionExpired/SessionExpired";
import ActivityTracker from "./components/ActivityTracker/ActivityTracker";
import MemberDetailsPage from "./pages/MemberDetailsPage";
import CagMappingsPage from "./pages/CagMappingsPage";
import ManualTasksPage from "./pages/ManualTasksPage";
import NotFoundPage from "./pages/NotFoundpage";
import AssessmentConfigPage from "./pages/clinicalNote/AssessmentConfigPage";
import { ROUTE_MODULE_MAPPING } from "./constants/modules";

import DashboardPage from "./pages/DashboardPage";
import LoginPage from "./pages/login";
import MainLayout from "./layouts/MainLayout";
import AccessRestricted from "./components/accessRestricted/AccessRestricted";
import PharmacyOutreach from "./components/pharmacyOutreach/pharmacyOutreach";
import MemberDetailsPharmacy from "./components/pharmacyOutreach/memberDetailsPahrmacy";
import MemberDetailsProvider from "./components/providerOutreach/memberDetailsProvider";
import ProviderOutreach from "./components/providerOutreach/providerOutreach";
import MemberReporting from "./components/reporting/memberReporting";
import NoRolePage from "./components/noRole/NoRole";
import AccountDisabled from "./components/accountDisabled/AccountDisabled";
import NoAccessPage from "./components/noAccess/NoAccess";
import Loader from "./components/common/Loader/Loader";
import PermissionBasedRedirect from "./components/common/PermissionBasedRedirect";

import { useMsal } from "@azure/msal-react";
import InvoiceReporting from "./components/reporting/invoiceReporting";
import SupervisorReporting from "./components/reporting/supervisorReporting";

import { InteractionStatus } from "@azure/msal-browser";

import { LoadingProvider, useLoading } from "./context/LoadingContext";

import { getProfilePhoto, loginApi } from "./api/login";
import ManagePermissions from "./components/manage-permissions/manage-permissions";

import MembersPage from "./pages/membersPage";

import { loginSuccess } from "./redux/slices/authSlice";
import InternationalSubmission from "./pages/InternationalSubmission";

export const serverAddress = "/landing/auth/callback";
export const qaEnv =
  process.env.REACT_APP_ENVIRONMENT === "qa" ||
  process.env.REACT_APP_ENVIRONMENT === "prod";

const ProtectedRoute = ({ children }) => {
  const location = useLocation();
  const { isAuthenticated, sessionExpired } = useSelector(
    (state) => state.auth,
  );
  const userDetails = JSON.parse(localStorage.getItem("userDetails") || "{}");
  const hasRoles = userDetails?.roles?.length > 0;
  const token = localStorage.getItem("token");
  const isAuthenticatedRedux = !!token && isAuthenticated;

  // Track if this tab was already active in the current session
  const isTabAlreadyActive = sessionStorage.getItem("isTabActive") === "true";

  // Check if session is marked as expired in localStorage
  const sessionExpiredLocal =
    sessionExpired || localStorage.getItem("sessionExpired") === "true";

  if (!token) {
    localStorage.clear();
    sessionStorage.clear();
    return <Navigate to="/landing" replace />;
  }

  // If session is expired, determine where to redirect
  if (sessionExpiredLocal) {
    // If this is a fresh navigation/manual entry (not an already active tab),
    // go to login instead of showing the session-timeout screen.
    if (!isTabAlreadyActive) {
      localStorage.clear();
      return <Navigate to="/landing" replace />;
    }
    // For already active tabs, show the session-timeout screen.
    return <Navigate to="/session-timeout" replace />;
  }

  // If not authenticated, redirect to login
  if (!isAuthenticatedRedux) {
    return <Navigate to="/landing" replace />;
  }

  // If no roles, redirect to no-role page
  if (!hasRoles) {
    return <Navigate to="/no-role" replace />;
  }

  const path = location.pathname.toLowerCase();
  const requiredModule = ROUTE_MODULE_MAPPING.find((m) =>
    m.patterns.some((p) => path.includes(p)),
  )?.module;

  if (requiredModule) {
    const modulePermissions = userDetails?.permissions?.[requiredModule] || {
      read: 0,
    };
    if (modulePermissions.read !== 1) {
      return <Navigate to="/not-found" replace />;
    }
  }

  // Mark tab as active since we are successfully accessing a protected route.
  // This helps distinguish this tab from fresh navigation in case of future expiration.
  sessionStorage.setItem("isTabActive", "true");

  // Add ActivityTracker to monitor user activity
  return (
    <>
      <ActivityTracker />
      {children}
    </>
  );
};

const LandingPage = () => {
  const { sessionExpired } = useSelector((state) => state.auth);

  const token = localStorage.getItem("token");
  const sessionExpiredLocal =
    sessionExpired || localStorage.getItem("sessionExpired") === "true";

  useEffect(() => {
    // Clear localStorage and sessionStorage if user is not authenticated
    if (!token) {
      localStorage.clear();
      sessionStorage.clear();
    }
  }, [token]);

  // If we have an active session, redirect to the main page
  if (token && !sessionExpiredLocal) {
    return <Navigate to="/members/member-outreach" replace />;
  }

  return <LoginPage />;
};

const AppRoutes = () => {
  const isMobileView = useMediaQuery("(max-width:899.95px)");

  return (
    <Routes>
      <Route
        path="/dashboard"
        element={
          <ProtectedRoute>
            <MainLayout>
              <DashboardPage />
            </MainLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/"
        element={<Navigate to="/members/member-outreach" replace />}
      />
      <Route
        path="/cg-config"
        element={
          <ProtectedRoute>
            <MainLayout isPriorityOutreach={true}>
              <AssessmentConfigPage />
            </MainLayout>
          </ProtectedRoute>
        }
      />

      <Route
        path="/cag-configuration"
        element={
          <ProtectedRoute>
            <MainLayout isPriorityOutreach={true}>
              <CagMappingsPage />
            </MainLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/members/member-outreach"
        element={
          <ProtectedRoute>
            <MainLayout isPriorityOutreach={true}>
              <MembersPage isPriorityOutreach={true} />
            </MainLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/pharmacy-outreach"
        element={
          <ProtectedRoute>
            <MainLayout>
              <PharmacyOutreach />
            </MainLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/provider-outreach"
        element={
          <ProtectedRoute>
            <MainLayout>
              <ProviderOutreach />
            </MainLayout>
          </ProtectedRoute>
        }
      />

      <Route
        path="/role-management"
        element={
          <ProtectedRoute>
            <MainLayout>
              <ManagePermissions />
            </MainLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/manual-tasks"
        element={
          <ProtectedRoute>
            <MainLayout isPriorityOutreach={true}>
              <ManualTasksPage />
            </MainLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/manual-tasks/:taskId"
        element={
          <ProtectedRoute>
            <MainLayout isPriorityOutreach={true}>
              <ManualTasksPage />
            </MainLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/international-submission"
        element={
          <ProtectedRoute>
            <MainLayout isPriorityOutreach={true}>
              <InternationalSubmission />
            </MainLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/reporting/member-reporting"
        element={
          <ProtectedRoute>
            <MainLayout>
              <MemberReporting />
            </MainLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/reporting/supervisor-reporting"
        element={
          <ProtectedRoute>
            <MainLayout>
              <SupervisorReporting />
            </MainLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/reporting/invoice-reporting"
        element={
          <ProtectedRoute>
            <MainLayout>
              <InvoiceReporting />
            </MainLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/members/:memberId"
        element={
          <ProtectedRoute>
            <AccessRestricted>
              <MainLayout isPriorityOutreach={!isMobileView}>
                <MemberDetailsPage />
              </MainLayout>
            </AccessRestricted>
          </ProtectedRoute>
        }
      />
      <Route
        path="/pharmacyProfile/:memberId"
        element={
          <ProtectedRoute>
            <MainLayout>
              <MemberDetailsPharmacy />
            </MainLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/providerProfile/:memberId"
        element={
          <ProtectedRoute>
            <MainLayout>
              <MemberDetailsProvider />
            </MainLayout>
          </ProtectedRoute>
        }
      />
      <Route path="/no-role" element={<NoRolePage />} />
      <Route path="/account-disabled" element={<AccountDisabled />} />
      <Route path="/no-access" element={<NoAccessPage />} />
      <Route path="/session-timeout" element={<SessionExpired />} />
      <Route path={serverAddress} element={<PermissionBasedRedirect />} />

      <Route path="/landing" element={<LandingPage />} />
      <Route
        path="*"
        element={
          <ProtectedRoute>
            <MainLayout hideSearch={true}>
              <NotFoundPage />
            </MainLayout>
          </ProtectedRoute>
        }
      />
    </Routes>
  );
};

const storeProfilePhotoIfAvailable = async (graphAccessToken) => {
  if (!graphAccessToken) return;
  try {
    const photoUrl = await getProfilePhoto(graphAccessToken);
    if (photoUrl) localStorage.setItem("profilePhoto", photoUrl);
  } catch (error_) {
    console.warn("Failed to load profile photo", error_);
  }
};

const performBackendLogin = async (graphAccessToken, userEmail) => {
  if (!graphAccessToken || !userEmail) return null;
  const resp = await loginApi({
    adoAccessToken: graphAccessToken,
    emailId: userEmail,
  });

  // Handle Scenario 1: User NOT in AD Group (403 status with USER_NOT_IN_AD_GROUP error)
  if (
    resp.status === 403 &&
    resp.data?.errors?.includes("USER_NOT_IN_AD_GROUP")
  ) {
    return {
      redirect: "no-access",
      message: resp.data.message || " ",
    };
  }

  // Handle Scenario 2: User HAS Access but NO Roles (200 status with empty roles array)
  if (resp.status === 200 && resp.data?.data?.roles?.length === 0) {
    const userData = resp.data.data;
    localStorage.setItem("userDetails", JSON.stringify(userData));
    return {
      redirect: "no-role",
      userData: userData,
    };
  }

  // Handle successful login with roles
  if (resp.status === 200 && resp.data?.data?.accessToken) {
    const userData = resp.data.data;
    localStorage.setItem("userDetails", JSON.stringify(userData));
    localStorage.setItem("token", userData.accessToken);
    return userData;
  }

  // Handle Scenario 3: User is disabled/inactive (404 status)
  if (resp.status === 404) {
    // Store minimal user details for the account disabled screen
    const minimalUserData = {
      emailId: userEmail,
      roles: [],
      primaryRoleId: null,
      primaryRoleName: null,
    };
    localStorage.setItem("userDetails", JSON.stringify(minimalUserData));
    return {
      redirect: "account-disabled",
      message: "Account is disabled",
      userData: minimalUserData,
    };
  }

  // Handle other errors
  return resp.data;
};

const dispatchLoginFromLocalStorage = (dispatch) => {
  const token = localStorage.getItem("token");
  if (!token) return false;
  dispatch(
    loginSuccess({
      token,
      tokenType: "Bearer",
      userDetails: JSON.parse(localStorage.getItem("userDetails") || "{}"),
    }),
  );
  return true;
};

const handleBackendResponse = (backend, dispatch, navigate, setLoginError) => {
  if (backend?.redirect === "no-access") {
    navigate("/no-access");
    return;
  }

  if (backend?.redirect === "account-disabled") {
    navigate("/account-disabled");
    return;
  }

  if (backend?.redirect === "no-role") {
    navigate("/no-role");
    return;
  }

  if (backend?.accessToken) {
    dispatch(
      loginSuccess({
        token: backend.accessToken,
        tokenType: "Bearer",
        userDetails: backend,
      }),
    );
    sessionStorage.setItem("isTabActive", "true");
    setLoginError("");
    return;
  }

  if (backend?.message) {
    setLoginError(backend.message || "Email is not registered");
    navigate("/landing");
  }
};

const handleLoginResponse = async ({
  response,
  dispatch,
  navigate,
  setLoginError,
  setIsInitializing,
}) => {
  if (response) {
    const graphAccessToken = response.accessToken;
    await storeProfilePhotoIfAvailable(graphAccessToken);
    const existingToken = localStorage.getItem("token");
    if (existingToken) {
      dispatchLoginFromLocalStorage(dispatch);
    } else {
      try {
        const backend = await performBackendLogin(
          graphAccessToken,
          response?.account?.username,
        );

        handleBackendResponse(backend, dispatch, navigate, setLoginError);
      } catch (e) {
        console.error(e);
        setLoginError("Email is not registered");
        localStorage.clear();
        navigate("/landing");
      }
    }
    setIsInitializing(false);
    return;
  }

  if (dispatchLoginFromLocalStorage(dispatch)) {
    setIsInitializing(false);
  } else {
    setIsInitializing(false);
    navigate("/landing");
    localStorage.clear();
  }
};

const handleLoginFailure = ({ navigate, setLoginError, setIsInitializing }) => {
  setIsInitializing(false);
  setLoginError("Email is not registered — no employee found.");
  navigate("/landing");
};

function deriveHeading(pathname) {
  const path = pathname.toLowerCase();
  if (path.includes("dashboard")) return "Dashboard";
  if (path.includes("cag-configuration")) return "CAG Configuration";
  if (path.includes("manual-tasks")) return "Manual Tasks";
  if (path.includes("role-management")) return "Role Management";
  if (path.includes("members") && path.includes("member-outreach"))
    return "Member Outreach";
  if (path.includes("pharmacy-outreach")) return "Pharmacy Outreach";
  if (path.includes("provider-outreach")) return "Provider Outreach";
  if (path.includes("pharmacyprofile")) return "Pharmacy Profile";
  if (path.includes("providerprofile")) return "Provider Profile";
  if (path.includes("landing")) return "Login";
  if (path === "/" || path === "") return "Dashboard";
  return "LucyRx Halo";
}

const cleanDomAriaLabels = (root = document) => {
  const selector = "div[aria-label]:not([role]), span[aria-label]:not([role])";
  try {
    const nodes = root.querySelectorAll(selector);
    for (const el of nodes) {
      const label = el.getAttribute("aria-label");
      if (!label) continue;
      if (!el.getAttribute("title")) el.setAttribute("title", label);
      el.removeAttribute("aria-label");
    }
  } catch (error_) {
    // Ignore DOM errors during cleanup
    console.debug("DOM cleanup error ignored:", error_);
  }
};

const handleAttributeMutation = (m) => {
  const el = m.target;
  if (
    el instanceof HTMLElement &&
    (el.tagName === "DIV" || el.tagName === "SPAN") &&
    !el.getAttribute("role")
  ) {
    const label = el.getAttribute("aria-label");
    if (label && !el.getAttribute("title")) el.setAttribute("title", label);
    el.removeAttribute("aria-label");
  }
};

const handleChildListMutation = (m) => {
  if (m.addedNodes?.length) {
    for (const node of m.addedNodes) {
      if (node.nodeType === Node.ELEMENT_NODE) cleanDomAriaLabels(node);
    }
  }
};

const handleDomMutation = (m) => {
  if (m.type === "attributes" && m.attributeName === "aria-label") {
    handleAttributeMutation(m);
  } else if (m.type === "childList") {
    handleChildListMutation(m);
  }
};

function App() {
  const dispatch = useDispatch();
  const { instance, inProgress } = useMsal();
  const navigate = useNavigate();
  const { loading } = useLoading();

  const [loginError, setLoginError] = useState("");
  const [isInitializing, setIsInitializing] = useState(true);

  useEffect(() => {
    // Back from the accounts page restores the old page from bfcache; reload to render landing
    const onPageShow = (e) => {
      if (e.persisted) {
        localStorage.clear();
        globalThis.location.replace("/landing");
      }
    };
    globalThis.addEventListener("pageshow", onPageShow);
    return () => globalThis.removeEventListener("pageshow", onPageShow);
  }, []);

  useEffect(() => {
    if (inProgress === InteractionStatus.Logout) {
      setIsInitializing(false);
      return;
    }
    if (inProgress !== InteractionStatus.None) return;
    instance
      .handleRedirectPromise()
      .then((response) =>
        handleLoginResponse({
          response,
          dispatch,
          navigate,
          setLoginError,
          setIsInitializing,
        }),
      )
      .catch(() =>
        handleLoginFailure({ navigate, setLoginError, setIsInitializing }),
      );
  }, [instance, inProgress, dispatch, navigate]);

  useEffect(() => {
    const needsLabel = (el) => {
      if (!el) return false;
      const aria = el.getAttribute("aria-label");
      const labelledBy = el.getAttribute("aria-labelledby");
      if (aria?.trim()) return false;
      if (labelledBy) {
        const hasAny = labelledBy.split(/\s+/).some((id) => {
          const ref = document.getElementById(id);
          return ref?.textContent.trim();
        });
        if (hasAny) return false;
      }
      return true;
    };

    const deriveText = (input) => {
      let text = "";
      const label = input.closest("label");
      if (label) text = label.textContent.trim();
      if (!text) {
        const parent = input.closest(
          ".MuiMenuItem-root, .MuiListItem-root, .MuiTableRow-root, div, td, th",
        );
        if (parent) {
          const candidate = parent.querySelector(
            "span, p, strong, .option-text",
          );
          if (candidate) text = candidate.textContent.trim();
          if (!text) {
            const texts = Array.from(parent.childNodes).filter(
              (n) => n.nodeType === Node.TEXT_NODE && n.textContent.trim(),
            );
            if (texts.length)
              text = texts.map((n) => n.textContent.trim()).join(" ");
          }
        }
      }
      if (!text) text = "Option";
      return text;
    };

    const labelInputs = () => {
      const inputs = document.querySelectorAll("input.PrivateSwitchBase-input");
      for (const input of inputs) {
        if (!needsLabel(input)) continue;
        const text = deriveText(input);
        input.setAttribute("aria-label", text);
      }
    };
    labelInputs();
    const observer = new MutationObserver(() => labelInputs());
    observer.observe(document.body, { childList: true, subtree: true });
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    cleanDomAriaLabels(document);
    const mo = new MutationObserver((mutations) => {
      for (const mutation of mutations) {
        handleDomMutation(mutation);
      }
    });
    mo.observe(document.body, {
      subtree: true,
      childList: true,
      attributes: true,
      attributeFilter: ["aria-label"],
    });
    return () => mo.disconnect();
  }, []);

  useEffect(() => {
    const updateVh = () => {
      document.documentElement.style.setProperty(
        "--real-vh",
        `${globalThis.innerHeight}px`,
      );
    };

    updateVh();
    globalThis.addEventListener("resize", updateVh);

    return () => globalThis.removeEventListener("resize", updateVh);
  }, []);

  // Listen for cross-tab session expiration
  useEffect(() => {
    const handleStorageChange = (e) => {
      if (e.key === "sessionExpired" && e.newValue === "true") {
        // Force re-render to show SessionExpired component
        globalThis.location.reload();
      }
    };

    globalThis.addEventListener("storage", handleStorageChange);
    return () => globalThis.removeEventListener("storage", handleStorageChange);
  }, []);

  if (isInitializing) {
    return (
      <ThemeProvider theme={theme}>
        <CssBaseline />
        <Loader open={true} />
      </ThemeProvider>
    );
  }

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />

      <a href="#main-content" id="skip-to-content-link">
        Skip to main content
      </a>
      <Loader open={loading} />
      <Snackbar
        open={!!loginError}
        autoHideDuration={6000}
        onClose={() => setLoginError("")}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert severity="error" onClose={() => setLoginError("")}>
          {loginError}
        </Alert>
      </Snackbar>

      <main id="main-content" tabIndex={-1}>
        <h1 className="visually-hidden">
          {deriveHeading(globalThis.location.pathname)}
        </h1>
        <AppRoutes />
      </main>
    </ThemeProvider>
  );
}

const AppWrapper = connect(
  (state) => ({
    isLoggedIn: state.auth?.isAuthenticated || false,
    appDataLoading: state.appearance?.appDataLoading,
    userDetails: state.auth?.userDetails,
  }),
  (dispatch) => ({
    setLoggedIn: (value) => {
      if (value) {
        dispatch(
          loginSuccess({
            token: localStorage.getItem("token"),
            tokenType: "Bearer",
            userDetails: JSON.parse(
              localStorage.getItem("userDetails") || "{}",
            ),
          }),
        );
      }
    },
  }),
)((props) => {
  const { setLoggedIn, isLoggedIn } = props;
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const token = localStorage.getItem("token");
    setLoggedIn(!!token);
  }, [setLoggedIn]);

  useEffect(() => {
    setLoading(true);
    setTimeout(() => setLoading(false), 100);
  }, [isLoggedIn]);

  return (
    <LoadingProvider>
      {loading ? <Loader open={true} /> : <App />}
    </LoadingProvider>
  );
});

export default AppWrapper;

ProtectedRoute.propTypes = {
  children: PropTypes.node.isRequired,
};
